import React, { FC, useEffect, useRef, useState } from 'react';
import arrowDown from '../../assets/icons/caret-down-primary.svg'

type CustomFieldsData = Record<string, string[]>;

interface ConnectedDropdownProps {
    data: CustomFieldsData;
    onChange?: (result: {
        field: string | null;
        values: string[];
    }) => void;
}

const CampaignCustomFieldSelect: FC<ConnectedDropdownProps> = ({ data, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeField, setActiveField] = useState<string | null>(null);
    const [selectedValuesByField, setSelectedValuesByField] = useState<Record<string, string[]>>({});

    const wrapperRef = useRef<HTMLDivElement>(null);

    const fields = Object.keys(data);
    const values = activeField ? data[activeField] ?? [] : [];
    const selectedValues = activeField ? (selectedValuesByField[activeField] ?? []) : [];

    useEffect(() => {
        onChange?.({
            field: activeField,
            values: selectedValues,
        });
    }, [activeField, selectedValuesByField, onChange]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleValue = (val: string) => {
        if (!activeField) return;
        setSelectedValuesByField((prev) => {
            const current = prev[activeField] ?? [];
            const nextValues = current.includes(val)
                ? current.filter((v) => v !== val)
                : [...current, val];
            return { ...prev, [activeField]: nextValues };
        });
    };

    const handleFieldClick = (field: string) => {
        if (field !== activeField) {
            setActiveField(field);
        }
    };

    return (
        <div ref={wrapperRef} className="relative w-[200px]">
            <button
                type="button"
                onClick={() => setIsOpen((o) => !o)}
                className="flex h-[40px] w-full items-center justify-between gap-2 rounded-[8px] border border-[#CEDADF] bg-white px-3 text-left text-sm text-primary hover:border-primary focus:outline-none focus:border-primary"
            >
                <span className="truncate">
                    {activeField ?? 'Select field'}
                </span>
                <img
                    src={arrowDown}
                    alt=""
                    className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div
                    className="absolute right-0 z-50 mt-2 flex rounded-[8px] border border-[#E0E0E0] bg-white shadow-lg"
                >
                    {activeField && (
                        <div
                            className="w-[220px] max-h-[260px] overflow-y-auto border-r border-[#E0E0E0] p-2 text-primary"
                        >
                            {values.map((val) => (
                                <label
                                    key={val}
                                    className="flex text-primary cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-[#F2F2F2]"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(val)}
                                        onChange={() => toggleValue(val)}
                                        className="h-4 w-4 accent-primary"
                                    />
                                    <span className="truncate">{val}</span>
                                </label>
                            ))}
                        </div>
                    )}

                    <div
                        className="w-[220px] max-h-[260px] overflow-y-auto p-2 text-primary"
                    >
                        {fields.map((field) => (
                            <div
                                key={field}
                                onClick={() => handleFieldClick(field)}
                                className={`cursor-pointer rounded-md px-3 py-2 text-sm hover:bg-[#F2F2F2] ${
                                    field === activeField ? 'bg-[#F2F2F2] font-medium' : ''
                                }`}
                            >
                                <span className="truncate">{field}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignCustomFieldSelect;
